import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { FONT_HEAD, FONT_BODY } from './styles'
import { FUNNEL_RESERVED } from './funnelReserved'

const PUBLIC_DOMAIN = 'truetopproducer.com'
const SLUG_RE = /^[a-z0-9-]+$/

function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const EMPTY_FORM = { agent_id: '', slug: '', zillow_url: '', lang_default: 'es' }

export default function FunnelsAdmin({ session, profile, showToast, navigateTo }) {
  const [loading, setLoading] = useState(true)
  const [funnels, setFunnels] = useState([])
  const [agents, setAgents] = useState([])            // sorted: selectable first, then disabled
  const [agentsById, setAgentsById] = useState({})    // id -> agent

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [savingForm, setSavingForm] = useState(false)

  const [togglingId, setTogglingId] = useState(null)
  const [fubBanner, setFubBanner] = useState(null)    // { source_tag, agent_name } | null

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [funnelsRes, agentsRes] = await Promise.all([
        supabase.from('funnels').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, full_name, email, role, brokerage, slug').in('role', ['agent', 'admin']).order('full_name'),
      ])
      if (funnelsRes.error) {
        console.error('[funnels load]', funnelsRes.error)
        showToast('Could not load funnels: ' + funnelsRes.error.message)
      }
      if (agentsRes.error) {
        console.error('[agents load]', agentsRes.error)
        showToast('Could not load agents: ' + agentsRes.error.message)
      }
      const rawAgents = agentsRes.data || []
      const byId = {}
      rawAgents.forEach(a => { byId[a.id] = a })
      // Sort: selectable (has brokerage) first, then disabled (no brokerage)
      const sortedAgents = [...rawAgents].sort((a, b) => {
        const aHas = !!(a.brokerage && String(a.brokerage).trim())
        const bHas = !!(b.brokerage && String(b.brokerage).trim())
        if (aHas !== bHas) return aHas ? -1 : 1
        return String(a.full_name || '').localeCompare(String(b.full_name || ''))
      })
      setAgents(sortedAgents)
      setAgentsById(byId)
      setFunnels(funnelsRes.data || [])
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Fire on mount only. Depending on `load` here would re-fire any time its
  // `showToast` dep changes ref — and App.js's showToast is a fresh function
  // on every Dashboard render. That cascade would wipe the open create form's
  // in-progress edits whenever a toast fires. Mirror OpenHouseAdmin's pattern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  // ── Form helpers ───────────────────────────────────────────────────
  function onAgentChange(newId) {
    // When agent changes, regenerate slug from their full_name.
    const a = agentsById[newId]
    // Suggested funnel slug is the agent's name; funnels live at /funnel/<slug>.
    const generated = a ? slugify(a.full_name) : ''
    setForm(f => ({ ...f, agent_id: newId, slug: generated }))
  }
  function onSlugChange(value) {
    setForm(f => ({ ...f, slug: value.toLowerCase() }))
  }
  function openCreate() {
    setEditingId(null)
    setForm({ ...EMPTY_FORM })
    setFormOpen(true)
  }
  function openEdit(funnel) {
    setEditingId(funnel.id)
    setForm({
      agent_id: funnel.agent_id || '',
      slug: funnel.slug || '',
      zillow_url: funnel.zillow_url || '',
      lang_default: funnel.lang_default || 'es',
    })
    setFormOpen(true)
  }
  function closeForm() {
    setFormOpen(false)
    setEditingId(null)
    setForm({ ...EMPTY_FORM })
  }

  // ── Derived ────────────────────────────────────────────────────────
  const slugTrim = form.slug.trim()
  const derivedSourceTag = slugTrim ? `funnel-${slugTrim}` : ''
  const selectedAgent = form.agent_id ? agentsById[form.agent_id] : null

  // ── Validation ─────────────────────────────────────────────────────
  let slugError = null
  if (!slugTrim) {
    slugError = 'Slug is required.'
  } else if (!SLUG_RE.test(slugTrim)) {
    slugError = 'Slug must be lowercase letters, numbers, and hyphens only.'
  } else if (FUNNEL_RESERVED.includes(slugTrim)) {
    slugError = 'That slug is reserved.'
  } else {
    const collision = funnels.find(f => String(f.slug || '').toLowerCase() === slugTrim && f.id !== editingId)
    if (collision) slugError = 'That slug is already in use.'
  }
  const agentError = !form.agent_id ? 'Select an agent.' : null
  const agentBrokerageError = selectedAgent && !(selectedAgent.brokerage && String(selectedAgent.brokerage).trim())
    ? 'Selected agent has no brokerage set; cannot create.'
    : null
  const isFormValid = !slugError && !agentError && !agentBrokerageError

  // ── Save ───────────────────────────────────────────────────────────
  async function saveForm() {
    if (!isFormValid || savingForm) return
    setSavingForm(true)
    try {
      const payload = {
        slug: slugTrim,
        agent_id: form.agent_id,
        zillow_url: form.zillow_url.trim() || null,
        lang_default: form.lang_default,
        source_tag: derivedSourceTag,
      }
      if (editingId) {
        const { error } = await supabase.from('funnels').update(payload).eq('id', editingId)
        if (error) throw error
        showToast('Funnel updated')
      } else {
        const { error } = await supabase.from('funnels').insert({ ...payload, is_active: true })
        if (error) throw error
        showToast('Funnel created')
        const agentName = selectedAgent?.full_name || selectedAgent?.email || 'the agent'
        setFubBanner({ source_tag: derivedSourceTag, agent_name: agentName })
      }
      closeForm()
      await load()
    } catch (e) {
      console.error('[funnels save]', e)
      showToast('Save failed: ' + (e.message || 'unknown'))
    } finally {
      setSavingForm(false)
    }
  }

  // ── Pause / activate ───────────────────────────────────────────────
  async function toggleActive(funnel) {
    if (togglingId) return
    setTogglingId(funnel.id)
    try {
      const next = funnel.is_active === false  // currently paused → activate
      const { error } = await supabase.from('funnels').update({ is_active: next }).eq('id', funnel.id)
      if (error) throw error
      showToast(next ? 'Funnel activated' : 'Funnel paused')
      await load()
    } catch (e) {
      console.error('[funnels toggle]', e)
      showToast('Toggle failed: ' + (e.message || 'unknown'))
    } finally {
      setTogglingId(null)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div>
        <div style={{padding:'8px 0 20px'}}>
          <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>Each funnel maps a public slug at <code style={{fontFamily:'monospace'}}>{PUBLIC_DOMAIN}/funnel/&lt;slug&gt;</code> to one agent. Leads captured on the landing page route to that agent in Follow Up Boss via the source tag.</div>
        </div>

        {fubBanner && (
          <FubReminderBanner
            sourceTag={fubBanner.source_tag}
            agentName={fubBanner.agent_name}
            onDismiss={() => setFubBanner(null)}
          />
        )}

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom:'16px'}}>
          <div style={{fontSize:'12px',color:'#888',fontFamily:FONT_BODY}}>
            {loading ? 'Loading…' : `${funnels.length} funnel${funnels.length === 1 ? '' : 's'}`}
          </div>
          {!formOpen && (
            <button
              onClick={openCreate}
              style={{
                padding:'10px 18px',
                borderRadius:'8px',
                border:'none',
                background:'#147efb',
                color:'white',
                fontWeight:800,
                fontFamily:FONT_HEAD,
                fontSize:'13px',
                letterSpacing:'0.5px',
                cursor:'pointer',
              }}
            >+ NEW FUNNEL</button>
          )}
        </div>

        {formOpen && (
          <CreateEditForm
            form={form}
            editingId={editingId}
            agents={agents}
            derivedSourceTag={derivedSourceTag}
            slugError={slugError}
            agentError={agentError}
            agentBrokerageError={agentBrokerageError}
            isFormValid={isFormValid}
            saving={savingForm}
            onAgentChange={onAgentChange}
            onSlugChange={onSlugChange}
            onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
            onSave={saveForm}
            onCancel={closeForm}
          />
        )}

        {loading ? (
          <div style={{padding:'40px 0',color:'#666',fontFamily:FONT_BODY}}>Loading funnels…</div>
        ) : funnels.length === 0 ? (
          <EmptyState />
        ) : (
          <FunnelsList
            funnels={funnels}
            agentsById={agentsById}
            onEdit={openEdit}
            onToggleActive={toggleActive}
            togglingId={togglingId}
          />
        )}
    </div>
  )
}

// ── FUB reminder banner ──────────────────────────────────────────────
function FubReminderBanner({ sourceTag, agentName, onDismiss }) {
  return (
    <div style={{
      background:'#fff8e1',
      border:'1px solid #fdbf00',
      borderLeft:'5px solid #fdbf00',
      borderRadius:'10px',
      padding:'14px 16px',
      marginBottom:'18px',
      fontFamily:FONT_BODY,
      color:'#1d2e42',
      display:'flex',
      gap:'12px',
      alignItems:'flex-start',
    }}>
      <div style={{fontSize:'22px',lineHeight:1}}>⚠️</div>
      <div style={{flex:1,fontSize:'13px',lineHeight:1.55}}>
        <div style={{fontWeight:800,marginBottom:'4px',fontFamily:FONT_HEAD,fontSize:'13px',letterSpacing:'0.5px'}}>
          One manual step left in Follow Up Boss
        </div>
        The first lead from this funnel will auto-create a Lead Flow row for source{' '}
        <code style={{fontFamily:'monospace',background:'rgba(0,0,0,0.06)',padding:'1px 6px',borderRadius:'4px'}}>{sourceTag}</code>.
        Open FUB → <strong>Admin → Lead Flow</strong> and set that row's Distribution to{' '}
        <strong>{agentName}</strong> — otherwise their first lead defaults to the account admin. This can only be done in FUB.
      </div>
      <button
        onClick={onDismiss}
        style={{
          background:'transparent',
          border:'none',
          color:'#475569',
          fontSize:'16px',
          cursor:'pointer',
          padding:'4px 8px',
          lineHeight:1,
        }}
        aria-label="Dismiss"
      >×</button>
    </div>
  )
}

// ── Create / edit form ───────────────────────────────────────────────
function CreateEditForm({ form, editingId, agents, derivedSourceTag, slugError, agentError, agentBrokerageError, isFormValid, saving, onAgentChange, onSlugChange, onChange, onSave, onCancel }) {
  return (
    <div style={{
      background:'white',
      border:'1px solid #e8ecf1',
      borderRadius:'12px',
      padding:'24px',
      marginBottom:'20px',
    }}>
      <div style={{fontSize:'12px',fontWeight:800,marginBottom:'18px',fontFamily:FONT_HEAD,color:'#1d2e42',letterSpacing:'2px'}}>
        {editingId ? 'EDIT FUNNEL' : 'NEW FUNNEL'}
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:'14px',maxWidth:'560px'}}>

        <Field label="AGENT" error={agentError || agentBrokerageError}>
          <select
            value={form.agent_id}
            onChange={e => onAgentChange(e.target.value)}
            style={selectStyle}
          >
            <option value="">— Select agent —</option>
            {agents.map(a => {
              const hasBrokerage = !!(a.brokerage && String(a.brokerage).trim())
              const baseName = a.full_name || a.email || '(no name)'
              const label = hasBrokerage
                ? `${baseName} · ${a.brokerage}`
                : `${baseName} — needs brokerage set`
              return (
                <option key={a.id} value={a.id} disabled={!hasBrokerage}>
                  {label}
                </option>
              )
            })}
          </select>
        </Field>

        <Field label="SLUG" error={slugError}>
          <input
            type="text"
            value={form.slug}
            onChange={e => onSlugChange(e.target.value)}
            placeholder="josh-marquez"
            style={inputStyle}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div style={{fontSize:'11px',color:'#888',marginTop:'6px',fontFamily:'monospace'}}>
            Preview: {PUBLIC_DOMAIN}/funnel/{form.slug || '…'}
          </div>
        </Field>

        <Field label="SOURCE TAG (auto)" hint="Derived from slug — used by FUB Lead Flow routing">
          <input
            type="text"
            value={derivedSourceTag}
            readOnly
            tabIndex={-1}
            style={{...inputStyle, background:'#f3f5f8', color:'#475569', cursor:'default'}}
          />
        </Field>

        <Field label="ZILLOW URL (optional)">
          <input
            type="text"
            value={form.zillow_url}
            onChange={e => onChange({ zillow_url: e.target.value })}
            placeholder="https://www.zillow.com/profile/..."
            style={inputStyle}
          />
        </Field>

        <Field label="DEFAULT LANGUAGE">
          <select
            value={form.lang_default}
            onChange={e => onChange({ lang_default: e.target.value })}
            style={selectStyle}
          >
            <option value="es">Español (es)</option>
            <option value="en">English (en)</option>
          </select>
        </Field>

        <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
          <button
            onClick={onSave}
            disabled={!isFormValid || saving}
            style={{
              padding:'10px 20px',
              borderRadius:'8px',
              border:'none',
              background: (isFormValid && !saving) ? '#147efb' : '#9fc3ef',
              color:'white',
              fontWeight:800,
              fontFamily:FONT_HEAD,
              fontSize:'13px',
              letterSpacing:'0.5px',
              cursor: (isFormValid && !saving) ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'SAVING…' : (editingId ? 'SAVE CHANGES' : 'CREATE FUNNEL')}
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              padding:'10px 20px',
              borderRadius:'8px',
              border:'1px solid #d0d7e2',
              background:'white',
              color:'#475569',
              fontWeight:700,
              fontFamily:FONT_HEAD,
              fontSize:'13px',
              letterSpacing:'0.5px',
              cursor:'pointer',
            }}
          >CANCEL</button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, error, children }) {
  return (
    <label style={{display:'flex',flexDirection:'column',gap:'5px',fontFamily:FONT_BODY}}>
      <span style={{fontSize:'10px',fontWeight:800,letterSpacing:'1.5px',color:'#475569',fontFamily:FONT_HEAD}}>{label}</span>
      {children}
      {hint && !error && <div style={{fontSize:'11px',color:'#888'}}>{hint}</div>}
      {error && <div style={{fontSize:'12px',color:'#e23744',fontWeight:600}}>{error}</div>}
    </label>
  )
}

const inputStyle = {
  font: 'inherit',
  height: '40px',
  padding: '0 12px',
  border: '1.5px solid #d9e2ec',
  borderRadius: '8px',
  background: 'white',
  color: '#1d2e42',
  boxSizing: 'border-box',
  outline: 'none',
}
const selectStyle = { ...inputStyle, paddingRight: '8px' }

// ── Empty state ──────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{
      padding:'48px 24px',
      textAlign:'center',
      background:'#f8fafc',
      border:'1px dashed #d0d7e2',
      borderRadius:'12px',
      color:'#475569',
      fontFamily:FONT_BODY,
    }}>
      <div style={{fontSize:'18px',fontWeight:700,color:'#1d2e42',marginBottom:'8px',fontFamily:FONT_HEAD}}>No funnels yet</div>
      <div style={{fontSize:'13px'}}>Use “+ NEW FUNNEL” above to create the first one.</div>
    </div>
  )
}

// ── List ─────────────────────────────────────────────────────────────
const COLS = '1fr 1.2fr 1.2fr 1.1fr 0.7fr 1.2fr 0.9fr'
// Seven columns, three of them holding slugs, source tags and URLs. Same floor
// the open house roster uses — below it the card scrolls rather than clipping
// Actions off the right edge.
const FUNNELS_MIN_WIDTH = '900px'

function FunnelsList({ funnels, agentsById, onEdit, onToggleActive, togglingId }) {
  return (
    <div style={{
      background:'white',
      border:'1px solid #e8ecf1',
      borderRadius:'12px',
      overflowX:'auto',
      overflowY:'hidden',
      WebkitOverflowScrolling:'touch',
    }}>
      {/* Header and rows share this wrapper so one scrollbar governs both and
          their columns stay in register. */}
      <div style={{minWidth: FUNNELS_MIN_WIDTH}}>
      <div style={{
        display:'grid',
        gridTemplateColumns: COLS,
        gap:'12px',
        padding:'12px 16px',
        background:'#f6f8fb',
        borderBottom:'1px solid #e8ecf1',
        fontSize:'10px',
        fontWeight:800,
        letterSpacing:'1.5px',
        color:'#64748b',
        fontFamily:FONT_HEAD,
        textTransform:'uppercase',
      }}>
        <div>Slug</div>
        <div>Agent</div>
        <div>Brokerage</div>
        <div>Source tag</div>
        <div>Status</div>
        <div>Public URL</div>
        <div>Actions</div>
      </div>
      {funnels.map(f => (
        <FunnelRow
          key={f.id}
          funnel={f}
          agent={agentsById[f.agent_id]}
          onEdit={() => onEdit(f)}
          onToggleActive={() => onToggleActive(f)}
          toggling={togglingId === f.id}
        />
      ))}
      </div>
    </div>
  )
}

function FunnelRow({ funnel, agent, onEdit, onToggleActive, toggling }) {
  const isActive = funnel.is_active !== false
  const agentName = agent ? (agent.full_name || agent.email || '—') : (funnel.agent_id ? '(unknown agent)' : '—')
  const brokerage = agent?.brokerage || '—'
  const publicUrl = `${PUBLIC_DOMAIN}/funnel/${funnel.slug || ''}`
  const publicHref = `https://${publicUrl}`

  return (
    <div style={{
      display:'grid',
      gridTemplateColumns: COLS,
      gap:'12px',
      padding:'14px 16px',
      borderBottom:'1px solid #f1f4f8',
      fontSize:'13px',
      fontFamily:FONT_BODY,
      color:'#1d2e42',
      alignItems:'center',
    }}>
      <div title={funnel.slug || ''} style={{fontWeight:700,fontFamily:'monospace',color:'#1d2e42',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{funnel.slug || '—'}</div>
      <div>{agentName}</div>
      <div style={{color:'#475569'}}>{brokerage}</div>
      <div title={funnel.source_tag || ''} style={{fontFamily:'monospace',fontSize:'12px',color:'#475569',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{funnel.source_tag || '—'}</div>
      <div>
        <span style={{
          display:'inline-block',
          padding:'3px 10px',
          borderRadius:'999px',
          fontSize:'10px',
          fontWeight:800,
          letterSpacing:'1px',
          fontFamily:FONT_HEAD,
          textTransform:'uppercase',
          background: isActive ? 'rgba(5,150,105,0.12)' : 'rgba(100,116,139,0.12)',
          color: isActive ? '#047857' : '#475569',
          border: isActive ? '1px solid rgba(5,150,105,0.35)' : '1px solid rgba(100,116,139,0.35)',
        }}>
          {isActive ? 'Active' : 'Paused'}
        </span>
      </div>
      <div>
        <a href={publicHref} target="_blank" rel="noopener noreferrer" title={publicUrl} style={{
          display:'block',
          color:'#147efb',
          textDecoration:'none',
          fontSize:'12px',
          whiteSpace:'nowrap',
          overflow:'hidden',
          textOverflow:'ellipsis',
        }}>{publicUrl}</a>
      </div>
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
        <button onClick={onEdit} style={rowBtnStyle('#147efb')}>Edit</button>
        <button
          onClick={onToggleActive}
          disabled={toggling}
          style={{...rowBtnStyle(isActive ? '#475569' : '#047857'), opacity: toggling ? 0.5 : 1, cursor: toggling ? 'wait' : 'pointer'}}
        >{toggling ? '…' : (isActive ? 'Pause' : 'Activate')}</button>
      </div>
    </div>
  )
}

function rowBtnStyle(color) {
  return {
    padding:'5px 10px',
    borderRadius:'6px',
    border:'1px solid ' + color,
    background:'white',
    color,
    fontSize:'11px',
    fontWeight:700,
    fontFamily:FONT_HEAD,
    letterSpacing:'0.5px',
    cursor:'pointer',
  }
}
